import { useState } from 'react';
import Icon from '@ant-design/icons';
import { Switch, Typography } from 'antd';
import { useTranslate } from '@tolgee/react';
import HighlightText from 'views/PureLLM/Components/HighlightText';
import { ReactComponent as FireSvg } from 'views/PureLLM/assets/fire.svg';

export interface ScenarioGroup {
  id: string;
  category: string;
  name: string;
  enabledCount: number;
  totalCount: number;
  hotCount: number;
  updateTime?: string | number;
  enabled: boolean;
}

export interface ScenarioGroupListProps {
  groups?: ScenarioGroup[];
  activeId?: string;
  keyword?: string;
  onSelect?: (id: string) => void;
  onToggle?: (id: string, enabled: boolean) => void;
  togglingIds?: string[];
}

function ScenarioGroupList(props: ScenarioGroupListProps) {
  const { t } = useTranslate();
  const {
    groups = [],
    activeId: controlledActiveId,
    keyword,
    onSelect,
    onToggle,
    togglingIds = [],
  } = props;

  const [internalActiveId, setInternalActiveId] = useState<string>();

  const activeId = controlledActiveId ?? internalActiveId;

  const handleSelect = (id: string) => {
    setInternalActiveId(id);
    onSelect?.(id);
  };

  const handleToggle = (id: string, checked: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle?.(id, checked);
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  return (
    <div data-sid="sgl-root-01" className="flex h-full flex-col overflow-y-auto bg-[#fcfcfc]">
      {groups.map((group) => {
        const isActive = group.id === activeId;
        const isToggling = togglingIds.includes(String(group.id));
        const textColor = group.enabled ? '#232426' : '#B5B9BF';
        return (
          <div
            data-sid="sgl-item-02"
            key={group.id}
            className="p-4 cursor-pointer transition-colors hover:bg-gray-50"
            style={{
              background: isActive ? '#F0F7FF' : '#fff',
              borderLeft: '2px solid',
              borderLeftColor: isActive ? 'var(--ant-primary-color)' : 'transparent',
              borderBottom: isActive ? 'none' : '1px solid #F0F2F5',
            }}
            onClick={() => handleSelect(group.id)}
          >
            <Typography.Paragraph
              data-sid="sgl-title-04"
              className="mb-1 text-sm font-medium"
              style={{ color: textColor, marginBottom: 0 }}
              ellipsis={{ rows: 3 }}
            >
              <HighlightText text={`${group.category} - ${group.name}`} keyword={keyword} />
            </Typography.Paragraph>
            <div data-sid="sgl-content-03" className="min-w-0">
              <div data-sid="sgl-stats-05" className="mb-1 text-xs" style={{ color: textColor }}>
                {t('scene.aiAgentConfig.scenarioList.description.enabledBranchCount', {
                  enableCount: group.enabledCount,
                  allCount: group.totalCount,
                })}
              </div>
              <div data-sid="sgl-meta-11">
                <div className="flex items-center justify-between gap-3">
                  <div
                    data-sid="sgl-hot-06"
                    className="flex items-center gap-1 text-xs"
                    style={{ color: textColor }}
                  >
                    <Icon data-sid="sgl-fire-07" component={FireSvg} style={{ fontSize: 14 }} />
                    <span data-sid="sgl-hotnum-08">{formatNumber(group.hotCount)}</span>
                  </div>
                  <div data-sid="sgl-switch-09" className="flex-shrink-0">
                    <Switch
                      data-sid="sgl-toggle-10"
                      checked={group.enabled}
                      loading={isToggling}
                      disabled={isToggling}
                      onChange={(checked, e) =>
                        handleToggle(group.id, checked, e as unknown as React.MouseEvent)
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default ScenarioGroupList;
